
import Vue from 'vue';
import { RECAPTCHA_CHALLENGE_SITE_KEY } from '@run-diffusion/shared';
import {
	hasUsableHumanCheckPass,
	HumanCheckBackgroundStatus,
	isHumanCheckChallengeConfigured,
	verifyHumanToken,
	VerifyHumanResult,
} from '@/services/humanCheck';
import DialogContent from '@/components/base/DialogContent.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import RecaptchaCheckbox from '@/components/RecaptchaCheckbox.vue';

type HumanCheckPhase = 'checking' | 'challenge' | 'verifying' | 'ready';

/*
The fallback half of the Human Check. It opens when a sign-in or sign-up click has no usable pass: it waits out an
in-flight invisible check, otherwise shows the checkbox challenge, and emits `on-continue` once the person may go on.
Continue is a real click on purpose, so an OAuth popup opened from it is not blocked by the browser. With
`autoContinue` (flows with no popup) it continues on its own as soon as the check clears.
 */
export default Vue.extend({
	name: 'HumanCheckDialog',
	props: {
		value: { type: Boolean, default: false },
		backgroundStatus: { type: String as () => HumanCheckBackgroundStatus, default: 'idle' },
		autoContinue: { type: Boolean, default: false },
	},
	data () {
		return {
			phase: 'checking' as HumanCheckPhase,
			errorMessage: null as string | null,
			RECAPTCHA_CHALLENGE_SITE_KEY,
		};
	},
	watch: {
		value: {
			immediate: true,
			handler (open: boolean) {
				if (open) this.start();
			},
		},
		backgroundStatus (status: HumanCheckBackgroundStatus) {
			if (this.value && this.phase === 'checking') this.applyBackgroundStatus(status);
		},
	},
	methods: {
		start () {
			this.errorMessage = null;
			this.phase = 'checking';
			this.applyBackgroundStatus(this.backgroundStatus);
		},
		applyBackgroundStatus (status: HumanCheckBackgroundStatus) {
			if (status === 'idle' || status === 'pending') return;
			if (status === 'passed' && hasUsableHumanCheckPass()) {
				this.setReady();
				return;
			}
			if (!isHumanCheckChallengeConfigured()) {
				// No challenge key yet, so there is nothing to show. The server still withholds the trial without a
				// verified pass, so letting the person through here gives nothing away.
				this.setReady();
				return;
			}
			this.phase = 'challenge';
		},
		setReady () {
			this.phase = 'ready';
			if (this.autoContinue) this.onContinue();
		},
		async onChallengeToken (token: string | null) {
			if (!token || this.phase !== 'challenge') return;
			this.phase = 'verifying';
			this.errorMessage = null;
			const result: VerifyHumanResult = await verifyHumanToken('challenge', token);
			if (!this.value) return;
			if (result !== 'failed') {
				// 'error' (our callable failed) and 'rate-limited' (often an office sharing one IP) must not lock a
				// real person out. Without a verified pass the server simply withholds the signup trial.
				this.setReady();
				return;
			}
			this.phase = 'challenge';
			this.errorMessage = 'We couldn\'t verify that. Please try again.';
			const recaptcha = this.$refs.recaptcha as { reset: () => void } | undefined;
			if (recaptcha) recaptcha.reset();
		},
		onContinue () {
			this.$emit('on-continue');
		},
		onCancel () {
			this.$emit('input', false);
		},
		onDialogInput (open: boolean) {
			if (!open) this.onCancel();
		},
	},
	components: {
		ActionsIsland,
		DialogContent,
		DialogOrBottomSheet,
		RecaptchaCheckbox,
	},
});
